import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../services/api';
import { 
  UploadCloud, CheckCircle, AlertCircle, RefreshCw, 
  FileJson, Radio, Trash2, Video, Plus, Shield, Globe, Layers, Cpu, Play,
  Smartphone, Camera, Mic, MicOff, Copy, Check, Info, BookOpen, QrCode, Download,
  Users, UserCheck, UserX
} from 'lucide-react';
import MarqueeTicker from '../components/MarqueeTicker';

const AdminUploadPortal: React.FC = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<'upload' | 'approval' | 'marquees' | 'news' | 'stream' | 'publications' | 'subscriptions' | 'registrations'>('upload');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam === 'stream' || tabParam === 'live') {
      setActiveTab('stream');
    } else if (tabParam === 'news' || tabParam === 'video' || tabParam === 'broadcast') {
      setActiveTab('news');
    } else if (tabParam === 'marquees' || tabParam === 'marquee') {
      setActiveTab('marquees');
    } else if (tabParam === 'approval' || tabParam === 'approve') {
      setActiveTab('approval');
    } else if (tabParam === 'publications' || tabParam === 'books' || tabParam === 'downloads') {
      setActiveTab('publications');
    } else if (tabParam === 'subscriptions' || tabParam === 'receipts' || tabParam === 'payments') {
      setActiveTab('subscriptions');
    } else if (tabParam === 'registrations' || tabParam === 'users' || tabParam === 'pending-users') {
      setActiveTab('registrations');
    } else if (tabParam === 'upload') {
      setActiveTab('upload');
    }
  }, [location.search]);

  // Mobile Device Live Streaming States
  const [streamSourceMode, setStreamSourceMode] = useState<'url' | 'mobile_camera' | 'rtmp_key'>('url');
  const [mobileFacingMode, setMobileFacingMode] = useState<'user' | 'environment'>('environment');
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [isMicMuted, setIsMicMuted] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);
  const [generatedStreamKey, setGeneratedStreamKey] = useState(`elum_live_${Date.now().toString(36)}`);
  
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);

  const startMobileCamera = async () => {
    try {
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(t => t.stop());
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: mobileFacingMode, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true
      });
      mediaStreamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
      setIsCameraActive(true);
      setStreamUrlInput('HTML5 Mobile WebRTC Camera & Mic Feed');
      setStatusMsg({ type: 'success', text: '📱 Mobile Camera & Mic Stream Active! Ready to broadcast live.' });
    } catch (err: any) {
      console.error('Camera access error:', err);
      setStatusMsg({ type: 'error', text: `Camera Error: ${err.message || 'Please grant camera & mic permissions in browser settings.'}` });
    }
  };

  const stopMobileCamera = () => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(t => t.stop());
      mediaStreamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsCameraActive(false);
  };

  const toggleCameraFacing = () => {
    const nextFacing = mobileFacingMode === 'user' ? 'environment' : 'user';
    setMobileFacingMode(nextFacing);
    if (isCameraActive) {
      setTimeout(() => startMobileCamera(), 200);
    }
  };

  const toggleMicMute = () => {
    if (mediaStreamRef.current) {
      const audioTracks = mediaStreamRef.current.getAudioTracks();
      audioTracks.forEach(track => {
        track.enabled = !track.enabled;
      });
      setIsMicMuted(!isMicMuted);
    }
  };

  const handleCopyStreamKey = () => {
    navigator.clipboard.writeText(generatedStreamKey);
    setCopiedKey(true);
    setTimeout(() => setCopiedKey(false), 2500);
  };
  
  // ----------------------------------------------------------------------
  // 1. Document Upload & Auto-Conversion States
  // ----------------------------------------------------------------------
  const [docFile, setDocFile] = useState<File | null>(null);
  const [docType, setDocType] = useState('Judgement');
  const [targetFormat, setTargetFormat] = useState('.json');
  const [title, setTitle] = useState('');
  const [citation, setCitation] = useState('');
  const [court, setCourt] = useState('Supreme Court of Pakistan');
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [summary, setSummary] = useState('');
  const [rawText, setRawText] = useState('');
  const [broadcastMarquee, setBroadcastMarquee] = useState(true);

  // ----------------------------------------------------------------------
  // 2. Non-Reported Staged Cases & Scraper States
  // ----------------------------------------------------------------------
  const [pendingCases, setPendingCases] = useState<any[]>([]);
  const [scraperStatus, setScraperStatus] = useState<any>(null);
  const [scraperLoading, setScraperLoading] = useState(false);

  // ----------------------------------------------------------------------
  // 3. Superuser Disposal Marquee 1 & Marquee 2 Custom Items States
  // ----------------------------------------------------------------------
  const [marqueeTarget, setMarqueeTarget] = useState<'marquee_1' | 'marquee_2'>('marquee_1');
  const [mItemTitle, setMItemTitle] = useState('');
  const [mItemLink, setMItemLink] = useState('');
  const [mItemImage, setMItemImage] = useState('');
  const [mItemSubtitle, setMItemSubtitle] = useState('');
  const [customMarqueeList, setCustomMarqueeList] = useState<any[]>([]);
  const [mPicFile, setMPicFile] = useState<File | null>(null);
  const [mVidFile, setMVidFile] = useState<File | null>(null);

  // ----------------------------------------------------------------------
  // 4. Video Clips & News Broadcast & Live Streaming States
  // ----------------------------------------------------------------------
  const [newsHeadline, setNewsHeadline] = useState('');
  const [newsSummary, setNewsSummary] = useState('');
  const [newsCategory, setNewsCategory] = useState<'news' | 'event' | 'act' | 'statute' | 'judgement' | 'video'>('news');
  const [videoUrl, setVideoUrl] = useState('');
  const [isLive, setIsLive] = useState(true);

  // Live Stream Event States (Superuser / DB Admin Only)
  const [streamTitle, setStreamTitle] = useState('');
  const [streamDesc, setStreamDesc] = useState('');
  const [streamTime, setStreamTime] = useState('');
  const [streamUrlInput, setStreamUrlInput] = useState('');
  const [liveStreamEvents, setLiveStreamEvents] = useState<any[]>([]);

  // Digital Publications & Books Ingestion States (Superuser & DB Admin)
  const [pubPdfFile, setPubPdfFile] = useState<File | null>(null);
  const [pubTitle, setPubTitle] = useState('');
  const [pubSubtitle, setPubSubtitle] = useState('');
  const [pubAuthors, setPubAuthors] = useState('');
  const [pubCategory, setPubCategory] = useState('CSS Examination & Islamic Jurisprudence');
  const [pubPrice, setPubPrice] = useState('1500');
  const [pubDescription, setPubDescription] = useState('');
  const [publicationsList, setPublicationsList] = useState<any[]>([]);
  const [pubLoading, setPubLoading] = useState(false);

  const fetchPublicationsList = async () => {
    try {
      const res = await api.get('/publications');
      if (res.data && res.data.publications) {
        setPublicationsList(res.data.publications);
      }
    } catch (err) {
      console.log('Error fetching publications list:', err);
    }
  };

  const handlePublishPublication = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pubPdfFile) {
      setStatusMsg({ type: 'error', text: 'Please select a PDF publication file to upload.' });
      return;
    }
    setPubLoading(true);
    setStatusMsg(null);
    try {
      const fd = new FormData();
      fd.append('pdf', pubPdfFile);
      fd.append('title', pubTitle);
      fd.append('subtitle', pubSubtitle);
      fd.append('authors', pubAuthors);
      fd.append('category', pubCategory);
      fd.append('price', pubPrice);
      fd.append('description', pubDescription);

      const token = localStorage.getItem('token');
      const res = await api.post('/publications/upload', fd, {
        headers: {
          'Content-Type': undefined,
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });
      setStatusMsg({ 
        type: 'success', 
        text: res.data.message || 'Publication successfully ingested, converted to JSON, and published with QR code!' 
      });
      setPubPdfFile(null);
      setPubTitle('');
      setPubSubtitle('');
      setPubAuthors('');
      setPubDescription('');
      fetchPublicationsList();
    } catch (err: any) {
      console.error('Publication Ingestion Error:', err);
      const errMsg = err.response?.data?.message || (err.response?.status === 413 ? 'Publication upload error: File size exceeds server capacity.' : (err.response?.status === 401 || err.response?.status === 403 ? 'Authentication required: Please log in with an Administrator, Superuser, or Senior Partner account.' : ('Publication ingestion failed: ' + (err.message || 'Unknown network error'))));
      setStatusMsg({ type: 'error', text: errMsg });
    } finally {
      setPubLoading(false);
    }
  };

  const [downloadingPubId, setDownloadingPubId] = useState<string | null>(null);

  const handleDownloadPublication = async (p: any) => {
    try {
      setDownloadingPubId(p._id);
      const token = localStorage.getItem('token');
      const res = await api.get(`/publications/${p._id}/download`, {
        params: { admin: 'true', token: token || undefined },
        responseType: 'blob'
      });

      if (res.data && res.data.type === 'application/json') {
        const text = await res.data.text();
        const json = JSON.parse(text);
        alert(json.message || 'Could not download PDF.');
        return;
      }

      const blob = new Blob([res.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', p.pdfFileName || `${(p.title || 'publication').replace(/[^a-zA-Z0-9_-]/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err: any) {
      console.error('Download error:', err);
      let errMsg = 'Failed to download publication PDF.';
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          if (json.message) errMsg = json.message;
        } catch (e) {}
      } else if (err.response?.data?.message) {
        errMsg = err.response.data.message;
      }
      alert(errMsg);
    } finally {
      setDownloadingPubId(null);
    }
  };

  const handleDeletePublication = async (id: string) => {
    if (!window.confirm('Are you sure you want to remove this publication from the Digital Library?')) return;
    try {
      await api.delete(`/publications/${id}`);
      setStatusMsg({ type: 'success', text: 'Publication removed from Digital Library.' });
      fetchPublicationsList();
    } catch (err: any) {
      setStatusMsg({ type: 'error', text: 'Failed to delete publication.' });
    }
  };

  // ----------------------------------------------------------------------
  // 7. Subscription Receipts & Payment Approvals States (Superuser / Owner)
  // ----------------------------------------------------------------------
  const [paymentsList, setPaymentsList] = useState<any[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [receiptModalUrl, setReceiptModalUrl] = useState<string | null>(null);
  const [approvingPaymentId, setApprovingPaymentId] = useState<string | null>(null);
  const [rejectingPaymentId, setRejectingPaymentId] = useState<string | null>(null);
  const [paymentFilter, setPaymentFilter] = useState<'all' | 'pending' | 'completed' | 'rejected'>('pending');

  const fetchPaymentsList = async () => {
    try {
      setPaymentsLoading(true);
      const res = await api.get('/payments');
      setPaymentsList(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.warn('Could not fetch payments list:', err);
    } finally {
      setPaymentsLoading(false);
    }
  };

  const handleApprovePayment = async (payment: any) => {
    try {
      setApprovingPaymentId(payment.id);
      const res = await api.patch(`/payments/${payment.id}/approve`, {
        comments: 'Confirmed and Approved by Owner / Superuser (Advocate Najeeb Ullah)'
      });
      setStatusMsg({
        type: 'success',
        text: res.data.message || 'Payment confirmed and publication download access unlocked for subscriber!'
      });
      fetchPaymentsList();
    } catch (err: any) {
      setStatusMsg({ type: 'error', text: err.response?.data?.message || 'Payment approval failed.' });
    } finally {
      setApprovingPaymentId(null);
    }
  };

  const handleRejectPayment = async (payment: any) => {
    const reason = window.prompt(
      'Enter reason for rejection (e.g. Amount not received in designated account, or TID invalid):',
      'Payment transfer could not be verified in Owner / Superuser designated account.'
    );
    if (!reason) return;

    try {
      setRejectingPaymentId(payment.id);
      const res = await api.patch(`/payments/${payment.id}/reject`, { reason });
      setStatusMsg({ type: 'success', text: res.data.message || 'Payment marked as rejected.' });
      fetchPaymentsList();
    } catch (err: any) {
      setStatusMsg({ type: 'error', text: err.response?.data?.message || 'Rejection failed.' });
    } finally {
      setRejectingPaymentId(null);
    }
  };

  // ----------------------------------------------------------------------
  // 8. User Registration Approvals States (Superuser / DB Admin)
  // ----------------------------------------------------------------------
  const [pendingUsers, setPendingUsers] = useState<any[]>([]);
  const [loadingPendingUsers, setLoadingPendingUsers] = useState(false);
  const [approvingUserId, setApprovingUserId] = useState<string | null>(null);
  const [rejectingUserId, setRejectingUserId] = useState<string | null>(null);
  const [selectedPendingUser, setSelectedPendingUser] = useState<any | null>(null);
  const [pendingUserRejectionComments, setPendingUserRejectionComments] = useState('');
  const [pendingUserDocPreview, setPendingUserDocPreview] = useState<string | null>(null);

  const fetchPendingUsers = async () => {
    try {
      setLoadingPendingUsers(true);
      const res = await api.get('/admin/pending-users');
      setPendingUsers(Array.isArray(res.data) ? res.data : []);
    } catch (err: any) {
      console.warn('Could not fetch pending users:', err);
      setPendingUsers([]);
    } finally {
      setLoadingPendingUsers(false);
    }
  };

  const handleApprovePendingUser = async (user: any) => {
    try {
      setApprovingUserId(user.id);
      const res = await api.post(`/admin/approve-user/${user.id}`);
      setStatusMsg({
        type: 'success',
        text: res.data.message || `Account for ${user.name} (${user.email}) has been approved and activated!`
      });
      setSelectedPendingUser(null);
      fetchPendingUsers();
    } catch (err: any) {
      setStatusMsg({ type: 'error', text: err.response?.data?.message || 'Failed to approve user registration.' });
    } finally {
      setApprovingUserId(null);
    }
  };

  const handleRejectPendingUser = async (user: any) => {
    const comments = window.prompt(
      `Enter reason for rejecting registration of ${user.name} (sent via email):`,
      pendingUserRejectionComments || 'Credentials or Bar License verification could not be validated.'
    );
    if (comments === null) return;

    try {
      setRejectingUserId(user.id);
      const res = await api.post(`/admin/reject-user/${user.id}`, { comments });
      setStatusMsg({
        type: 'success',
        text: res.data.message || `Registration for ${user.name} has been rejected.`
      });
      setSelectedPendingUser(null);
      setPendingUserRejectionComments('');
      fetchPendingUsers();
    } catch (err: any) {
      setStatusMsg({ type: 'error', text: err.response?.data?.message || 'Failed to reject user registration.' });
    } finally {
      setRejectingUserId(null);
    }
  };

  // Global Status & Feedback
  const [loading, setLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [convertedResult, setConvertedResult] = useState<any | null>(null);
  const [publishedNews, setPublishedNews] = useState<any[]>([]);

  // Initial Data Fetching
  useEffect(() => {
    fetchPendingCases();
    fetchCustomMarquees();
    fetchPublishedNews();
    fetchScraperInfo();
    fetchLiveStreamEvents();
    fetchPublicationsList();
    fetchPaymentsList();
    fetchPendingUsers();
  }, []);

  const fetchLiveStreamEvents = async () => {
    try {
      const res = await api.get('/streams/active');
      setLiveStreamEvents(res.data || []);
    } catch (err) {
      console.log('Error fetching live streams:', err);
    }
  };

  const handleCreateLiveStream = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!streamTitle || !streamDesc) {
      setStatusMsg({ type: 'error', text: 'Title and Brief Description are required for Live Streaming.' });
      return;
    }

    try {
      setLoading(true);
      const res = await api.post('/streams/create', {
        title: streamTitle,
        description: streamDesc,
        scheduledTime: streamTime || new Date().toISOString(),
        streamUrl: streamUrlInput
      });

      setStatusMsg({ 
        type: 'success', 
        text: '🔴 Live event stream scheduled successfully! Automated 30-minute prior alerts (Email, Portal, WhatsApp, SMS) queued for all registered non-client users.' 
      });

      setStreamTitle('');
      setStreamDesc('');
      setStreamTime('');
      setStreamUrlInput('');
      fetchLiveStreamEvents();
    } catch (err: any) {
      setStatusMsg({ type: 'error', text: err.response?.data?.message || 'Failed to schedule live stream event.' });
    } finally {
      setLoading(false);
    }
  };

  const handleEndStreamAndPurge = async (id: string) => {
    try {
      setLoading(true);
      const res = await api.post(`/streams/${id}/end`);
      setStatusMsg({ 
        type: 'success', 
        text: 'Live stream ended! MP4 recording compiled. Click "Download MP4 Record" to save to local storage. File will be automatically purged from VPS cloud disk upon download.' 
      });
      fetchLiveStreamEvents();
    } catch (err: any) {
      setStatusMsg({ type: 'error', text: 'Error ending live stream.' });
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingCases = async () => {
    try {
      const res = await api.get('/admin/pending-cases');
      setPendingCases(res.data || []);
    } catch (err) {
      console.log('Error fetching pending cases:', err);
    }
  };

  const fetchCustomMarquees = async () => {
    try {
      const res = await api.get('/admin/marquee-custom');
      setCustomMarqueeList(res.data || []);
    } catch (err) {
      console.log('Error fetching custom marquee items:', err);
    }
  };

  const fetchPublishedNews = async () => {
    try {
      const res = await api.get('/admin/news');
      setPublishedNews(res.data || []);
    } catch (err) {
      console.log('Error fetching news list:', err);
    }
  };

  const fetchScraperInfo = async () => {
    try {
      const res = await api.get('/admin/scraper-status');
      setScraperStatus(res.data || null);
    } catch (err) {
      console.log('Error fetching scraper status:', err);
    }
  };

  // ----------------------------------------------------------------------
  // HANDLER: Document Upload & Auto-Conversion
  // ----------------------------------------------------------------------
  const handleDocumentUploadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!docFile && !rawText && !title) {
      setStatusMsg({ type: 'error', text: 'Please select a file or enter document title and text.' });
      return;
    }

    setLoading(true);
    setStatusMsg(null);
    setConvertedResult(null);

    try {
      const formData = new FormData();
      if (docFile) formData.append('file', docFile);
      formData.append('docType', docType);
      formData.append('targetFormat', targetFormat);
      formData.append('title', title);
      formData.append('citation', citation);
      formData.append('court', court);
      formData.append('year', year);
      formData.append('summary', summary);
      formData.append('rawText', rawText);
      formData.append('broadcastMarquee', broadcastMarquee ? 'true' : 'false');

      const token = localStorage.getItem('token');
      const response = await api.post('/admin/upload-document', formData, {
        headers: {
          'Content-Type': undefined,
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });

      setConvertedResult(response.data.convertedJson);
      setStatusMsg({ 
        type: 'success', 
        text: `Success! File converted to ${targetFormat} and synced to VPS Database. ${broadcastMarquee ? 'Broadcasted live on Marquee Ticker.' : ''}` 
      });

      // Reset fields
      setDocFile(null);
      setTitle('');
      setCitation('');
      setSummary('');
      setRawText('');

    } catch (err: any) {
      console.error('Upload Error:', err);
      const errMsg = err.response?.data?.message || (err.response?.status === 413 ? 'File too large for upload.' : (err.response?.status === 401 || err.response?.status === 403 ? 'Authentication required: Please log in with an Administrator or Superuser account.' : 'Document upload and conversion failed.'));
      setStatusMsg({ type: 'error', text: errMsg });
    } finally {
      setLoading(false);
    }
  };

  // ----------------------------------------------------------------------
  // HANDLER: Approve Staged Non-Reported Case for VPS DB Ingestion
  // ----------------------------------------------------------------------
  const handleApprovePendingCase = async (id: string) => {
    try {
      setLoading(true);
      const res = await api.post(`/admin/approve-case/${id}`, { broadcastMarquee: true });
      setStatusMsg({ type: 'success', text: res.data.message || 'Non-reported case approved and ingested into VPS database!' });
      fetchPendingCases();
    } catch (err: any) {
      setStatusMsg({ type: 'error', text: err.response?.data?.message || 'Approval failed.' });
    } finally {
      setLoading(false);
    }
  };

  const handleRejectPendingCase = async (id: string) => {
    try {
      const res = await api.post(`/admin/reject-case/${id}`);
      setStatusMsg({ type: 'success', text: res.data.message || 'Staged case rejected.' });
      fetchPendingCases();
    } catch (err: any) {
      setStatusMsg({ type: 'error', text: 'Rejection failed.' });
    }
  };

  const handleTriggerScraperNow = async () => {
    try {
      setScraperLoading(true);
      setStatusMsg({ 
        type: 'success', 
        text: 'Crawling designated superior courts (Supreme Court, FCC, FSC, BHC, LHC, SHC, PHC, IHC) and synchronizing live marquees...' 
      });
      const res = await api.post('/admin/trigger-scraper');
      setStatusMsg({ 
        type: 'success', 
        text: res.data.message || 'Daily automated court scraper executed successfully! New authoritative judgments auto-ingested to Elum Law Directory, and Marquees 1 & 2 updated.' 
      });
      fetchPendingCases();
      fetchScraperInfo();
      fetchCustomMarquees();
      fetchPublishedNews();
    } catch (err: any) {
      console.error('Scraper trigger error:', err);
      const errMsg = err.response?.data?.message || (err.response?.status === 401 || err.response?.status === 403 ? 'Authentication required: Please log in with an Administrator, Superuser, or Senior Partner account.' : 'Court scraper trigger failed: ' + (err.message || 'Server error'));
      setStatusMsg({ type: 'error', text: errMsg });
    } finally {
      setScraperLoading(false);
    }
  };

  // ----------------------------------------------------------------------
  // HANDLER: Superuser Disposal Marquee 1 & Marquee 2 Items Management
  // ----------------------------------------------------------------------
  const handleCreateCustomMarqueeItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mItemTitle) {
      setStatusMsg({ type: 'error', text: 'Please enter a title for the marquee item.' });
      return;
    }

    try {
      setLoading(true);
      const res = await api.post('/admin/marquee-custom', {
        targetMarquee: marqueeTarget,
        title: mItemTitle,
        linkUrl: mItemLink,
        imageUrl: mItemImage,
        subtitle: mItemSubtitle,
        isLive: true
      });

      setStatusMsg({ type: 'success', text: res.data.message || 'Marquee item published at Superuser disposal!' });
      setMItemTitle('');
      setMItemLink('');
      setMItemImage('');
      setMItemSubtitle('');
      fetchCustomMarquees();
    } catch (err: any) {
      setStatusMsg({ type: 'error', text: 'Failed to save marquee item.' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCustomMarqueeItem = async (id: string) => {
    try {
      await api.delete(`/admin/marquee-custom/${id}`);
      setStatusMsg({ type: 'success', text: 'Marquee item deleted.' });
      fetchCustomMarquees();
    } catch (err: any) {
      setStatusMsg({ type: 'error', text: 'Delete failed.' });
    }
  };

  // ----------------------------------------------------------------------
  // HANDLER: News & Video Clips Broadcast
  // ----------------------------------------------------------------------
  const handleNewsBroadcastSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newsHeadline || !newsSummary) {
      setStatusMsg({ type: 'error', text: 'Headline and Summary are required.' });
      return;
    }

    setLoading(true);
    setStatusMsg(null);

    try {
      const formData = new FormData();
      formData.append('headline', newsHeadline);
      formData.append('summary', newsSummary);
      formData.append('category', newsCategory);
      formData.append('videoUrl', videoUrl);
      formData.append('isLive', isLive ? 'true' : 'false');

      const response = await api.post('/admin/news', formData);
      setStatusMsg({ type: 'success', text: response.data.message || 'Live video news broadcast published successfully!' });
      
      setNewsHeadline('');
      setNewsSummary('');
      setVideoUrl('');
      fetchPublishedNews();
    } catch (err: any) {
      setStatusMsg({ type: 'error', text: 'Failed to publish news broadcast.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc', fontFamily: "'Inter', sans-serif" }}>
      
      {/* Live Marquee Ticker */}
      <MarqueeTicker />

      {/* Header Banner */}
      <header style={{ 
        backgroundColor: '#0f172a', 
        color: 'white', 
        padding: '1.5rem 2rem', 
        borderBottom: '3px solid #ecc94b',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <img 
              src="/logo_ELum_Firm4.png" 
              alt="Elum Law Firm Logo" 
              style={{ height: '38px', width: '38px', objectFit: 'contain', borderRadius: '50%', border: '1.5px solid #ecc94b', backgroundColor: '#020617', flexShrink: 0 }} 
            />
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <span style={{ backgroundColor: '#ecc94b', color: '#0f172a', fontSize: '0.7rem', fontWeight: '800', padding: '0.15rem 0.5rem', borderRadius: '4px', textTransform: 'uppercase' }}>
                  RESTRICTED ACCESS
                </span>
                <h1 style={{ fontSize: '1.25rem', fontWeight: '800', margin: 0, color: 'white', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <Shield size={20} color="#ecc94b" /> Database Administrator & Superuser Portal
                </h1>
              </div>
              <p style={{ color: '#94a3b8', fontSize: '0.8rem', margin: '0.2rem 0 0 0' }}>
                Auto-Conversion Engine, VPS DB Synchronization, 24-Hour Daily Court Judgment Crawlers, and Marquee Disposal Manager
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button
              onClick={handleTriggerScraperNow}
              disabled={scraperLoading}
              style={{
                backgroundColor: '#0284c7', color: 'white', border: 'none', padding: '0.6rem 1.1rem', borderRadius: '6px', cursor: scraperLoading ? 'not-allowed' : 'pointer', fontWeight: '700', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.4rem'
              }}
            >
              <Cpu size={16} /> {scraperLoading ? 'Crawling Supreme Court & High Courts...' : 'Run Daily Court Scraper & Auto-Ingest Now'}
            </button>
          </div>
        </div>
      </header>

      {/* Main Container */}
      <div style={{ maxWidth: '1200px', margin: '2rem auto', padding: '0 1rem' }}>
        
        {/* Navigation Tabs */}
        <div style={{ display: 'flex', gap: '0.5rem', borderBottom: '2px solid #e2e8f0', marginBottom: '1.5rem', overflowX: 'auto', paddingBottom: '0.5rem' }}>
          
          <button
            onClick={() => setActiveTab('upload')}
            style={{
              padding: '0.75rem 1.25rem',
              borderRadius: '8px 8px 0 0',
              border: 'none',
              backgroundColor: activeTab === 'upload' ? '#0284c7' : '#e2e8f0',
              color: activeTab === 'upload' ? 'white' : '#475569',
              fontWeight: '800',
              fontSize: '0.9rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              whiteSpace: 'nowrap'
            }}
          >
            <UploadCloud size={18} /> 1. Auto-Convert & Update VPS DB (.JSON)
          </button>

          <button
            onClick={() => setActiveTab('approval')}
            style={{
              padding: '0.75rem 1.25rem',
              borderRadius: '8px 8px 0 0',
              border: 'none',
              backgroundColor: activeTab === 'approval' ? '#e11d48' : '#e2e8f0',
              color: activeTab === 'approval' ? 'white' : '#475569',
              fontWeight: '800',
              fontSize: '0.9rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              whiteSpace: 'nowrap'
            }}
          >
            <Shield size={18} /> 2. Staged Non-Reported Cases Approval ({pendingCases.length})
          </button>

          <button
            onClick={() => setActiveTab('marquees')}
            style={{
              padding: '0.75rem 1.25rem',
              borderRadius: '8px 8px 0 0',
              border: 'none',
              backgroundColor: activeTab === 'marquees' ? '#d97706' : '#e2e8f0',
              color: activeTab === 'marquees' ? 'white' : '#475569',
              fontWeight: '800',
              fontSize: '0.9rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              whiteSpace: 'nowrap'
            }}
          >
            <Layers size={18} /> 3. Marquee 1 & 2 Disposal Manager ({customMarqueeList.length})
          </button>

          <button
            onClick={() => setActiveTab('news')}
            style={{
              padding: '0.75rem 1.25rem',
              borderRadius: '8px 8px 0 0',
              border: 'none',
              backgroundColor: activeTab === 'news' ? '#7c3aed' : '#e2e8f0',
              color: activeTab === 'news' ? 'white' : '#475569',
              fontWeight: '800',
              fontSize: '0.9rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              whiteSpace: 'nowrap'
            }}
          >
            <Video size={18} /> 4. Video Clips & News Broadcast
          </button>

          <button
            onClick={() => setActiveTab('stream' as any)}
            style={{
              padding: '0.75rem 1.25rem',
              borderRadius: '8px 8px 0 0',
              border: 'none',
              backgroundColor: activeTab === ('stream' as any) ? '#dc2626' : '#e2e8f0',
              color: activeTab === ('stream' as any) ? 'white' : '#475569',
              fontWeight: '800',
              fontSize: '0.9rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              whiteSpace: 'nowrap'
            }}
          >
            <Radio size={18} /> 5. Live Judicial Event Video Streaming ({liveStreamEvents.length})
          </button>

          <button
            onClick={() => setActiveTab('publications' as any)}
            style={{
              padding: '0.75rem 1.25rem',
              borderRadius: '8px 8px 0 0',
              border: 'none',
              backgroundColor: activeTab === ('publications' as any) ? '#9333ea' : '#e2e8f0',
              color: activeTab === ('publications' as any) ? 'white' : '#475569',
              fontWeight: '800',
              fontSize: '0.9rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              whiteSpace: 'nowrap'
            }}
          >
            <BookOpen size={18} /> 6. Publications & Books Ingestion ({publicationsList.length})
          </button>

          <button
            onClick={() => setActiveTab('subscriptions')}
            style={{
              padding: '0.75rem 1.25rem',
              borderRadius: '8px 8px 0 0',
              border: 'none',
              backgroundColor: activeTab === 'subscriptions' ? '#059669' : '#e2e8f0',
              color: activeTab === 'subscriptions' ? 'white' : '#475569',
              fontWeight: '800',
              fontSize: '0.9rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              whiteSpace: 'nowrap'
            }}
          >
            <span>💳 7. Subscription Receipts & Approvals</span>
            {paymentsList.filter(p => p.status === 'PENDING_APPROVAL').length > 0 && (
              <span style={{
                backgroundColor: '#dc2626',
                color: 'white',
                fontSize: '0.7rem',
                padding: '0.15rem 0.5rem',
                borderRadius: '10px',
                fontWeight: '900'
              }}>
                {paymentsList.filter(p => p.status === 'PENDING_APPROVAL').length} PENDING
              </span>
            )}
          </button>

          <button
            onClick={() => setActiveTab('registrations')}
            style={{
              padding: '0.75rem 1.25rem',
              borderRadius: '8px 8px 0 0',
              border: 'none',
              backgroundColor: activeTab === 'registrations' ? '#2563eb' : '#e2e8f0',
              color: activeTab === 'registrations' ? 'white' : '#475569',
              fontWeight: '800',
              fontSize: '0.9rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              whiteSpace: 'nowrap'
            }}
          >
            <Users size={18} />
            <span>8. User Registration Approvals</span>
            {pendingUsers.length > 0 && (
              <span style={{
                backgroundColor: '#dc2626',
                color: 'white',
                fontSize: '0.7rem',
                padding: '0.15rem 0.5rem',
                borderRadius: '10px',
                fontWeight: '900'
              }}>
                {pendingUsers.length} PENDING
              </span>
            )}
          </button>

        </div>

        {/* Status Message */}
        {statusMsg && (
          <div style={{ 
            backgroundColor: statusMsg.type === 'success' ? '#f0fdf4' : '#fef2f2', 
            borderLeft: `5px solid ${statusMsg.type === 'success' ? '#16a34a' : '#dc2626'}`, 
            padding: '1rem 1.25rem', 
            borderRadius: '8px', 
            marginBottom: '1.5rem',
            color: statusMsg.type === 'success' ? '#15803d' : '#991b1b',
            fontWeight: '700',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            {statusMsg.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
            {statusMsg.text}
          </div>
        )}

        {/* TAB 1: Document Upload & Auto-Conversion Panel */}
        {activeTab === 'upload' && (
          <div style={{ backgroundColor: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', padding: '2rem', boxSizing: 'border-box' }}>
            <div style={{ marginBottom: '1.5rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '1rem' }}>
              <h2 style={{ fontSize: '1.35rem', fontWeight: '800', color: '#0f172a', margin: '0 0 0.25rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <UploadCloud size={24} color="#0284c7" /> Auto-Convert & Update VPS Database
              </h2>
              <p style={{ color: '#64748b', fontSize: '0.9rem', margin: 0 }}>
                Upload any Act, Statute, Judgement, or Article from PC, Mac, Android, or iOS. Converts through your selected format dropdown into a structured <strong>.JSON</strong> legal schema and syncs directly to the live VPS Database.
              </p>
            </div>

            <form onSubmit={handleDocumentUploadSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              
              <div style={{ backgroundColor: '#f0f9ff', padding: '1.5rem', borderRadius: '12px', border: '2px dashed #0284c7', textAlign: 'center', boxSizing: 'border-box' }}>
                <UploadCloud size={40} color="#0284c7" style={{ marginBottom: '0.5rem' }} />
                <label style={{ display: 'block', fontSize: '1rem', fontWeight: '800', color: '#0369a1', cursor: 'pointer', marginBottom: '0.25rem' }}>
                  Select File from Device (PC / Android / iOS)
                </label>
                <input
                  type="file"
                  onChange={(e) => setDocFile(e.target.files ? e.target.files[0] : null)}
                  accept=".pdf,.doc,.docx,.txt,.json"
                  style={{ display: 'inline-block', fontSize: '0.9rem' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '800', color: '#0284c7', marginBottom: '0.45rem' }}>
                    🎯 Dropdown Conversion Target Format
                  </label>
                  <select
                    value={targetFormat}
                    onChange={(e) => setTargetFormat(e.target.value)}
                    style={{ width: '100%', height: '46px', padding: '0 0.85rem', borderRadius: '8px', border: '2px solid #0284c7', fontSize: '0.95rem', fontWeight: '800', backgroundColor: '#f0f9ff', color: '#0369a1', boxSizing: 'border-box' }}
                  >
                    <option value=".json">.JSON (Structured Legal Database Schema)</option>
                    <option value="PDF">PDF (Formatted Appellate Document)</option>
                    <option value="DOCX">DOCX (Editable Word Document)</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '800', color: '#475569', marginBottom: '0.45rem' }}>
                    📜 Document Type
                  </label>
                  <select
                    value={docType}
                    onChange={(e) => setDocType(e.target.value)}
                    style={{ width: '100%', height: '46px', padding: '0 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem', fontWeight: '700', backgroundColor: 'white', boxSizing: 'border-box' }}
                  >
                    <option value="Judgement">Judgement / Judicial Precedent</option>
                    <option value="Act">Act / Legislative Act</option>
                    <option value="Statute">Statute / Statutory Regulation</option>
                    <option value="Article">Legal Article / Publication</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.25rem' }}>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Document / Case Title"
                  style={{ width: '100%', height: '44px', padding: '0 0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', boxSizing: 'border-box' }}
                />
                <input
                  type="text"
                  value={citation}
                  onChange={(e) => setCitation(e.target.value)}
                  placeholder="Citation (e.g. 2026 SCMR 101)"
                  style={{ width: '100%', height: '44px', padding: '0 0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', boxSizing: 'border-box' }}
                />
              </div>

              <textarea
                rows={3}
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                placeholder="Executive Summary / Principles..."
                style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', boxSizing: 'border-box' }}
              />

              <button
                type="submit"
                disabled={loading}
                style={{ padding: '0.85rem 2rem', borderRadius: '8px', border: 'none', backgroundColor: '#0284c7', color: 'white', cursor: loading ? 'not-allowed' : 'pointer', fontWeight: '800', fontSize: '1rem' }}
              >
                {loading ? 'Converting & Updating VPS Database...' : 'Convert to .JSON & Update VPS Database'}
              </button>
            </form>
          </div>
        )}

        {/* TAB 2: Staged Non-Reported Cases Approval Center */}
        {activeTab === 'approval' && (
          <div style={{ backgroundColor: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', padding: '2rem', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '1rem' }}>
              <div>
                <h2 style={{ fontSize: '1.35rem', fontWeight: '800', color: '#e11d48', margin: '0 0 0.25rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Shield size={24} color="#e11d48" /> Weekly Crawler Staged Non-Reported Cases Approval Center
                </h2>
                <p style={{ color: '#64748b', fontSize: '0.9rem', margin: 0 }}>
                  <strong>Reported cases</strong> from Supreme Court, <strong>Federal Constitutional Court (FCC - <a href="https://www.fccp.gov.pk/judgments" target="_blank" rel="noreferrer" style={{ color: '#e11d48', textDecoration: 'underline' }}>https://www.fccp.gov.pk/judgments</a>)</strong>, <strong>Federal Shariat Court (FSC - <a href="https://www.federalshariatcourt.gov.pk/en/home/" target="_blank" rel="noreferrer" style={{ color: '#e11d48', textDecoration: 'underline' }}>https://www.federalshariatcourt.gov.pk/en/home/</a>)</strong>, and High Courts are automatically ingested into VPS DB. <strong>Non-reported cases</strong> require Superuser or Database Administrator approval before ingestion.
                </p>
              </div>
              <button
                onClick={fetchPendingCases}
                style={{ padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #cbd5e1', backgroundColor: 'white', cursor: 'pointer', fontWeight: '700', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
              >
                <RefreshCw size={14} /> Refresh List
              </button>
            </div>

            {pendingCases.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem 0', color: '#64748b', fontWeight: '600' }}>
                🎉 No non-reported cases pending approval. Run the Weekly Scraper to fetch latest court decisions!
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                {pendingCases.map((c) => (
                  <div key={c._id} style={{ backgroundColor: '#fff1f2', borderRadius: '12px', border: '1px solid #fecdd3', padding: '1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                    <div style={{ flex: 1, minWidth: '280px' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.35rem' }}>
                        <span style={{ backgroundColor: '#e11d48', color: 'white', fontSize: '0.7rem', padding: '0.15rem 0.5rem', borderRadius: '4px', fontWeight: '800' }}>
                          NON-REPORTED (STAGED)
                        </span>
                        <span style={{ fontSize: '0.8rem', fontWeight: '700', color: '#475569' }}>
                          🏛️ {c.court}
                        </span>
                      </div>
                      <h4 style={{ fontSize: '1.1rem', fontWeight: '800', color: '#0f172a', margin: '0 0 0.35rem 0' }}>
                        {c.title}
                      </h4>
                      <p style={{ fontSize: '0.9rem', color: '#334155', margin: '0 0 0.5rem 0' }}>
                        {c.summary}
                      </p>
                      {c.sourceUrl && (
                        <a href={c.sourceUrl} target="_blank" rel="noreferrer" style={{ fontSize: '0.8rem', color: '#0284c7', fontWeight: '700' }}>
                          📄 View Source PDF
                        </a>
                      )}
                    </div>

                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={() => handleApprovePendingCase(c._id)}
                        disabled={loading}
                        style={{ padding: '0.6rem 1.2rem', borderRadius: '6px', border: 'none', backgroundColor: '#16a34a', color: 'white', cursor: 'pointer', fontWeight: '800', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
                      >
                        <CheckCircle size={16} /> Approve & Ingest to VPS DB
                      </button>
                      <button
                        onClick={() => handleRejectPendingCase(c._id)}
                        style={{ padding: '0.6rem 1rem', borderRadius: '6px', border: '1px solid #fecdd3', backgroundColor: 'white', color: '#dc2626', cursor: 'pointer', fontWeight: '700', fontSize: '0.85rem' }}
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* TAB 3: Superuser Disposal of Marquee 1 & Marquee 2 */}
        {activeTab === 'marquees' && (
          <div style={{ backgroundColor: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', padding: '2rem', boxSizing: 'border-box' }}>
            <div style={{ marginBottom: '1.5rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '1rem' }}>
              <h2 style={{ fontSize: '1.35rem', fontWeight: '800', color: '#d97706', margin: '0 0 0.25rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Layers size={24} color="#d97706" /> Superuser Disposal: Marquee 1 & Marquee 2 Custom Items Manager
              </h2>
              <p style={{ color: '#64748b', fontSize: '0.9rem', margin: 0 }}>
                Superusers and Database Administrators have full control to publish, edit, or remove items for <strong>Marquee 1 (Reported Cases Bar)</strong> and <strong>Marquee 2 (Recent Events & Pictures Bar)</strong> on the Landing Page.
              </p>
            </div>

            <form onSubmit={handleCreateCustomMarqueeItem} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', backgroundColor: '#fffbeb', padding: '1.5rem', borderRadius: '12px', border: '1px solid #fef3c7', marginBottom: '2rem' }}>
              <div style={{ backgroundColor: '#fef2f2', borderLeft: '4px solid #dc2626', padding: '0.85rem 1rem', borderRadius: '6px', color: '#991b1b', fontSize: '0.85rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Shield size={18} color="#dc2626" />
                <span>RESTRICTED PROVISION: Updating Marquee 2 (Event Pictures, Video Clips & Brief Descriptions) is strictly limited to <strong>Superuser</strong> or <strong>Database Administrator</strong> accounts only.</span>
              </div>

              <h4 style={{ fontSize: '1rem', fontWeight: '800', color: '#92400e', margin: 0 }}>
                ➕ Publish New Item to Marquee 1 or Marquee 2
              </h4>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '800', color: '#92400e', marginBottom: '0.45rem' }}>
                    Select Target Marquee
                  </label>
                  <select
                    value={marqueeTarget}
                    onChange={(e) => setMarqueeTarget(e.target.value as any)}
                    style={{ width: '100%', height: '46px', padding: '0 0.85rem', borderRadius: '8px', border: '2px solid #d97706', fontSize: '0.95rem', fontWeight: '800', backgroundColor: 'white', color: '#92400e', boxSizing: 'border-box' }}
                  >
                    <option value="marquee_1">Marquee 1: Reported Cases Bar (Red Badge)</option>
                    <option value="marquee_2">Marquee 2: Events, Pictures & Video Clips Bar (Blue Badge - Superuser / DB Admin Only)</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '800', color: '#475569', marginBottom: '0.45rem' }}>
                    Title / Event Headline
                  </label>
                  <input
                    type="text"
                    required
                    value={mItemTitle}
                    onChange={(e) => setMItemTitle(e.target.value)}
                    placeholder="e.g. 📸 Judicial Conference on Digital Law Trees"
                    style={{ width: '100%', height: '46px', padding: '0 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.25rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '800', color: '#0369a1', marginBottom: '0.45rem' }}>
                    🖼️ Event Picture / Thumbnail (URL or Device Upload)
                  </label>
                  <input
                    type="text"
                    value={mItemImage}
                    onChange={(e) => setMItemImage(e.target.value)}
                    placeholder="https://example.com/court-event-picture.jpg"
                    style={{ width: '100%', height: '44px', padding: '0 0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', boxSizing: 'border-box', marginBottom: '0.5rem' }}
                  />
                  <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>📱 Or Upload Photo from Memory / Mobile Camera:</div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setMPicFile(e.target.files ? e.target.files[0] : null)}
                    style={{ fontSize: '0.8rem', color: '#0369a1' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '800', color: '#7c3aed', marginBottom: '0.45rem' }}>
                    🎬 Video Clip (URL or Device Upload)
                  </label>
                  <input
                    type="text"
                    value={videoUrl}
                    onChange={(e) => setVideoUrl(e.target.value)}
                    placeholder="https://youtube.com/watch?v=video_clip_id"
                    style={{ width: '100%', height: '44px', padding: '0 0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', boxSizing: 'border-box', marginBottom: '0.5rem' }}
                  />
                  <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>📹 Or Upload Video Clip from Memory / Mobile Camera:</div>
                  <input
                    type="file"
                    accept="video/*"
                    onChange={(e) => setMVidFile(e.target.files ? e.target.files[0] : null)}
                    style={{ fontSize: '0.8rem', color: '#7c3aed' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '800', color: '#475569', marginBottom: '0.45rem' }}>
                    🔗 Target Action / External Web Link
                  </label>
                  <input
                    type="text"
                    value={mItemLink}
                    onChange={(e) => setMItemLink(e.target.value)}
                    placeholder="https://supremecourt.gov.pk/event-details"
                    style={{ width: '100%', height: '44px', padding: '0 0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '800', color: '#334155', marginBottom: '0.45rem' }}>
                  📝 Brief Description of Event / Proceeding
                </label>
                <textarea
                  rows={2}
                  value={mItemSubtitle}
                  onChange={(e) => setMItemSubtitle(e.target.value)}
                  placeholder="Provide a brief description of the event, conference, or court video clip..."
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', boxSizing: 'border-box' }}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{ padding: '0.75rem 1.5rem', borderRadius: '8px', border: 'none', backgroundColor: '#d97706', color: 'white', cursor: 'pointer', fontWeight: '800', fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '0.4rem', alignSelf: 'flex-start' }}
              >
                <Plus size={16} /> Publish to Selected Marquee (Superuser / DB Admin Only)
              </button>
            </form>

            {/* List of Custom Items */}
            <h4 style={{ fontSize: '1.1rem', fontWeight: '800', color: '#0f172a', marginBottom: '1rem' }}>
              Active Items Managed at Superuser Disposal ({customMarqueeList.length})
            </h4>

            {customMarqueeList.length === 0 ? (
              <div style={{ color: '#64748b', padding: '1rem 0' }}>
                No custom items created yet. Default reported cases and events are currently showing.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {customMarqueeList.map((item) => (
                  <div key={item._id} style={{ backgroundColor: '#f8fafc', padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
                    <div>
                      <span style={{ backgroundColor: item.targetMarquee === 'marquee_1' ? '#dc2626' : '#2563eb', color: 'white', fontSize: '0.7rem', padding: '0.15rem 0.5rem', borderRadius: '4px', fontWeight: '800', marginRight: '0.5rem' }}>
                        {item.targetMarquee.toUpperCase()}
                      </span>
                      <strong style={{ fontSize: '0.95rem', color: '#0f172a' }}>{item.title}</strong>
                      {item.subtitle && <span style={{ fontSize: '0.85rem', color: '#64748b', marginLeft: '0.5rem' }}>({item.subtitle})</span>}
                    </div>

                    <button
                      onClick={() => handleDeleteCustomMarqueeItem(item._id)}
                      style={{ padding: '0.4rem 0.75rem', borderRadius: '6px', border: '1px solid #fecdd3', backgroundColor: '#fff1f2', color: '#be123c', cursor: 'pointer', fontWeight: '700', fontSize: '0.8rem' }}
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* TAB 4: Video Clips & Live News Broadcast */}
        {activeTab === 'news' && (
          <div style={{ backgroundColor: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', padding: '2rem', boxSizing: 'border-box' }}>
            <div style={{ marginBottom: '1.5rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '1rem' }}>
              <h2 style={{ fontSize: '1.35rem', fontWeight: '800', color: '#7c3aed', margin: '0 0 0.25rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Video size={24} color="#7c3aed" /> Video Clips & Associated Live News Control Panel
              </h2>
              <p style={{ color: '#64748b', fontSize: '0.9rem', margin: 0 }}>
                Superuser & Data Administrator tool to publish <strong>Video Broadcast Clips</strong>, <strong>Judicial News</strong>, and <strong>Press Releases</strong> live on the marquee ticker.
              </p>
            </div>

            <form onSubmit={handleNewsBroadcastSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              <input
                type="text"
                required
                value={newsHeadline}
                onChange={(e) => setNewsHeadline(e.target.value)}
                placeholder="News / Video Headline Title"
                style={{ width: '100%', height: '46px', padding: '0 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem', boxSizing: 'border-box' }}
              />

              <input
                type="text"
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
                placeholder="Video URL / Embed Link (YouTube, Vimeo, MP4)"
                style={{ width: '100%', height: '46px', padding: '0 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem', boxSizing: 'border-box' }}
              />

              <textarea
                rows={3}
                required
                value={newsSummary}
                onChange={(e) => setNewsSummary(e.target.value)}
                placeholder="News Brief Summary to scroll across Marquee Ticker..."
                style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem', boxSizing: 'border-box' }}
              />

              <button
                type="submit"
                disabled={loading}
                style={{ padding: '0.85rem 2rem', borderRadius: '8px', border: 'none', backgroundColor: '#7c3aed', color: 'white', cursor: loading ? 'not-allowed' : 'pointer', fontWeight: '800', fontSize: '1rem' }}
              >
                {loading ? 'Publishing Live News...' : 'Publish Video & Live News Broadcast'}
              </button>
            </form>
          </div>
        )}

        {/* TAB 5: Live Judicial Event Video Streaming (Superuser / DB Admin Only) */}
        {activeTab === ('stream' as any) && (
          <div style={{ backgroundColor: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', padding: '2rem', boxSizing: 'border-box' }}>
            <div style={{ marginBottom: '1.5rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '1rem' }}>
              <div style={{ backgroundColor: '#fef2f2', borderLeft: '4px solid #dc2626', padding: '0.85rem 1rem', borderRadius: '6px', color: '#991b1b', fontSize: '0.85rem', fontWeight: '700', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Shield size={18} color="#dc2626" />
                <span>RESTRICTED BROADCASTING PROVISION: Live event video streaming is strictly limited to <strong>Superuser</strong> and <strong>Database Administrator</strong> accounts. Viewable by ALL registered users EXCEPT clients. Zero VPS cloud disk bloat architecture (temp recording auto-purged upon admin download).</span>
              </div>

              <h2 style={{ fontSize: '1.35rem', fontWeight: '800', color: '#dc2626', margin: '0 0 0.25rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Radio size={24} color="#dc2626" /> Live Special Event Video Streaming Control Center
              </h2>
              <p style={{ color: '#64748b', fontSize: '0.9rem', margin: 0 }}>
                Schedule or launch YouTube/HLS style live judicial broadcasts. Automatically sends <strong>30-Minute Prior Alerts</strong> (Email, Portal, WhatsApp, SMS) to all registered practitioners, judges, and law firm members.
              </p>
            </div>

            <form onSubmit={handleCreateLiveStream} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', backgroundColor: '#fff1f2', padding: '1.5rem', borderRadius: '12px', border: '1px solid #fecdd3', marginBottom: '2rem' }}>
              <h4 style={{ fontSize: '1rem', fontWeight: '800', color: '#9f1239', margin: 0, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                🔴 Schedule / Launch New Live Judicial Event
              </h4>

              {/* DUAL BROADCAST MODE SELECTOR */}
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', backgroundColor: '#ffe4e6', padding: '0.5rem', borderRadius: '10px' }}>
                <button
                  type="button"
                  onClick={() => { setStreamSourceMode('url'); stopMobileCamera(); }}
                  style={{
                    flex: 1, minWidth: '200px', padding: '0.65rem 1rem', borderRadius: '8px', border: 'none',
                    backgroundColor: streamSourceMode === 'url' ? '#dc2626' : 'white',
                    color: streamSourceMode === 'url' ? 'white' : '#991b1b',
                    fontWeight: '800', fontSize: '0.85rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem'
                  }}
                >
                  <Video size={16} /> 1. External Embed (YouTube / HLS URL)
                </button>

                <button
                  type="button"
                  onClick={() => { setStreamSourceMode('mobile_camera'); startMobileCamera(); }}
                  style={{
                    flex: 1, minWidth: '200px', padding: '0.65rem 1rem', borderRadius: '8px', border: 'none',
                    backgroundColor: streamSourceMode === 'mobile_camera' ? '#dc2626' : 'white',
                    color: streamSourceMode === 'mobile_camera' ? 'white' : '#991b1b',
                    fontWeight: '800', fontSize: '0.85rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem'
                  }}
                >
                  <Smartphone size={16} /> 2. 📱 Mobile Device Direct Camera Stream
                </button>

                <button
                  type="button"
                  onClick={() => { setStreamSourceMode('rtmp_key'); stopMobileCamera(); }}
                  style={{
                    flex: 1, minWidth: '200px', padding: '0.65rem 1rem', borderRadius: '8px', border: 'none',
                    backgroundColor: streamSourceMode === 'rtmp_key' ? '#dc2626' : 'white',
                    color: streamSourceMode === 'rtmp_key' ? 'white' : '#991b1b',
                    fontWeight: '800', fontSize: '0.85rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem'
                  }}
                >
                  <Radio size={16} /> 3. 🔑 RTMP Server / Mobile App Stream Key
                </button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '800', color: '#9f1239', marginBottom: '0.45rem' }}>
                    Event Title / Stream Headline
                  </label>
                  <input
                    type="text"
                    required
                    value={streamTitle}
                    onChange={(e) => setStreamTitle(e.target.value)}
                    placeholder="e.g. 🏛️ Full Court Address by CJP / High Court Bar Convention"
                    style={{ width: '100%', height: '46px', padding: '0 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', boxSizing: 'border-box' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '800', color: '#475569', marginBottom: '0.45rem' }}>
                    📅 Scheduled Start Time (Auto 30-Min Prior Alerts Triggered)
                  </label>
                  <input
                    type="datetime-local"
                    value={streamTime}
                    onChange={(e) => setStreamTime(e.target.value)}
                    style={{ width: '100%', height: '46px', padding: '0 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              {/* MODE 1: EXTERNAL EMBED URL */}
              {streamSourceMode === 'url' && (
                <div>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '800', color: '#0369a1', marginBottom: '0.45rem' }}>
                    🎥 Live Stream Embed / YouTube / HLS Manifest URL
                  </label>
                  <input
                    type="text"
                    value={streamUrlInput}
                    onChange={(e) => setStreamUrlInput(e.target.value)}
                    placeholder="https://youtube.com/embed/live_stream_id or https://www.elumlaw.com/live/hls/stream.m3u8"
                    style={{ width: '100%', height: '44px', padding: '0 0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', boxSizing: 'border-box' }}
                  />
                </div>
              )}

              {/* MODE 2: DIRECT MOBILE DEVICE CAMERA & MIC STREAM PREVIEW */}
              {streamSourceMode === 'mobile_camera' && (
                <div style={{ backgroundColor: '#0f172a', padding: '1.25rem', borderRadius: '12px', border: '1px solid #334155', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <div style={{ color: '#ecc94b', fontSize: '0.9rem', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      📱 HTML5 Mobile WebRTC Camera & Mic Feed
                    </div>
                    
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        type="button"
                        onClick={toggleCameraFacing}
                        style={{ padding: '0.4rem 0.85rem', backgroundColor: '#1e293b', color: 'white', border: '1px solid #475569', borderRadius: '6px', fontSize: '0.78rem', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                      >
                        🔄 Switch Camera ({mobileFacingMode === 'user' ? 'Front' : 'Rear Courtroom'})
                      </button>

                      <button
                        type="button"
                        onClick={toggleMicMute}
                        style={{ padding: '0.4rem 0.85rem', backgroundColor: isMicMuted ? '#dc2626' : '#16a34a', color: 'white', border: 'none', borderRadius: '6px', fontSize: '0.78rem', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                      >
                        {isMicMuted ? <MicOff size={14} /> : <Mic size={14} />} {isMicMuted ? 'Muted' : 'Mic Live'}
                      </button>
                    </div>
                  </div>

                  {/* HTML5 VIDEO STREAM PREVIEW */}
                  <div style={{ width: '100%', height: '240px', backgroundColor: '#000000', borderRadius: '8px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                    <video
                      ref={videoRef}
                      playsInline
                      muted={isMicMuted}
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                    {!isCameraActive && (
                      <div style={{ position: 'absolute', color: '#94a3b8', fontSize: '0.85rem', textAlign: 'center', padding: '1rem' }}>
                        📷 Camera Feed Offline. Click button below to start mobile stream.
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    {!isCameraActive ? (
                      <button
                        type="button"
                        onClick={startMobileCamera}
                        style={{ padding: '0.6rem 1.25rem', backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '6px', fontWeight: '800', fontSize: '0.85rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                      >
                        <Camera size={16} /> Activate Mobile Device Camera Feed
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={stopMobileCamera}
                        style={{ padding: '0.6rem 1.25rem', backgroundColor: '#dc2626', color: 'white', border: 'none', borderRadius: '6px', fontWeight: '800', fontSize: '0.85rem', cursor: 'pointer' }}
                      >
                        Stop Mobile Camera
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* MODE 3: MOBILE APP RTMP INGEST & STREAM KEY GENERATOR */}
              {streamSourceMode === 'rtmp_key' && (
                <div style={{ backgroundColor: '#070b12', padding: '1.25rem', borderRadius: '12px', border: '1px solid #1e293b', display: 'flex', flexDirection: 'column', gap: '0.75rem', fontSize: '0.85rem' }}>
                  <div style={{ color: '#38bdf8', fontWeight: '800', fontSize: '0.9rem' }}>
                    🔑 RTMP Mobile App Ingest Credentials (Larix Broadcaster, CameraFi, OBS Studio)
                  </div>
                  <div>
                    <span style={{ color: '#94a3b8' }}>RTMP Ingest Server URL:</span> <strong style={{ color: '#ecc94b' }}>rtmp://www.elumlaw.com/live</strong>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <span style={{ color: '#94a3b8' }}>Stream Key:</span>
                    <strong style={{ color: '#4ade80', fontFamily: 'monospace' }}>{generatedStreamKey}</strong>
                    <button
                      type="button"
                      onClick={handleCopyStreamKey}
                      style={{ padding: '0.25rem 0.65rem', backgroundColor: '#1e293b', color: 'white', border: '1px solid #334155', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}
                    >
                      {copiedKey ? <Check size={12} color="#4ade80" /> : <Copy size={12} />} {copiedKey ? 'Copied!' : 'Copy Stream Key'}
                    </button>
                  </div>
                </div>
              )}

              {/* MOBILE DEVICE HARDWARE & SYSTEM REQUIREMENTS SPECIFICATIONS */}
              <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', padding: '1rem', borderRadius: '10px', fontSize: '0.82rem', color: '#166534' }}>
                <div style={{ fontWeight: '800', marginBottom: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <Info size={16} color="#166534" /> 📱 Mobile Device Hardware & System Requirements for Live Streaming:
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.5rem', color: '#15803d' }}>
                  <div>• <strong>OS Compatible:</strong> Android 8.0+ (Chrome) / iOS 13.0+ (Safari).</div>
                  <div>• <strong>Min Upload Bandwidth:</strong> 5 Mbps 4G / 5G / Wi-Fi Upload Speed.</div>
                  <div>• <strong>Camera Resolution:</strong> 720p HD @ 30fps / 1080p Full HD.</div>
                  <div>• <strong>Audio Requirement:</strong> Noise-Canceling Built-in Mic or Lavalier.</div>
                  <div>• <strong>Recommended View:</strong> Landscape Orientation for Courtrooms.</div>
                  <div>• <strong>Security Protocol:</strong> SSL WebRTC & RTMP Encryption Protocol.</div>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '800', color: '#334155', marginBottom: '0.45rem' }}>
                  📝 Brief Description of Live Judicial Proceeding
                </label>
                <textarea
                  rows={3}
                  required
                  value={streamDesc}
                  onChange={(e) => setStreamDesc(e.target.value)}
                  placeholder="Detailed brief description of proceedings, agenda, and speakers..."
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', boxSizing: 'border-box' }}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{ padding: '0.85rem 2rem', borderRadius: '8px', border: 'none', backgroundColor: '#dc2626', color: 'white', cursor: 'pointer', fontWeight: '800', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', alignSelf: 'flex-start' }}
              >
                <Radio size={18} /> Schedule Stream & Dispatch 30-Min Prior Alerts
              </button>
            </form>

            {/* List of Active / Scheduled Streams */}
            <h4 style={{ fontSize: '1.1rem', fontWeight: '800', color: '#0f172a', marginBottom: '1rem' }}>
              Scheduled & Live Events Broadcasts ({liveStreamEvents.length})
            </h4>

            {liveStreamEvents.length === 0 ? (
              <div style={{ color: '#64748b', padding: '1rem 0' }}>
                No active or scheduled streams right now. Use the form above to schedule a live judicial event.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {liveStreamEvents.map((ev) => (
                  <div key={ev._id} style={{ backgroundColor: '#f8fafc', padding: '1.25rem', borderRadius: '12px', border: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                    <div>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.35rem' }}>
                        <span style={{ backgroundColor: ev.status === 'LIVE' ? '#dc2626' : '#2563eb', color: 'white', fontSize: '0.7rem', padding: '0.15rem 0.5rem', borderRadius: '4px', fontWeight: '800' }}>
                          {ev.status}
                        </span>
                        <span style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: '700' }}>
                          ⏰ Scheduled: {new Date(ev.scheduledTime).toLocaleString()}
                        </span>
                      </div>
                      <h4 style={{ fontSize: '1.05rem', fontWeight: '800', color: '#0f172a', margin: '0 0 0.35rem 0' }}>
                        {ev.title}
                      </h4>
                      <p style={{ fontSize: '0.85rem', color: '#475569', margin: 0 }}>
                        {ev.description}
                      </p>
                    </div>

                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {ev.status === 'LIVE' && (
                        <button
                          onClick={() => handleEndStreamAndPurge(ev._id)}
                          style={{ padding: '0.5rem 1rem', borderRadius: '6px', border: 'none', backgroundColor: '#dc2626', color: 'white', fontWeight: '800', cursor: 'pointer', fontSize: '0.85rem' }}
                        >
                          End Stream & Compile Temp MP4
                        </button>
                      )}

                      {ev.mp4RecordingUrl && (
                        <a
                          href={`/api/streams/download-recording/${ev._id}`}
                          download
                          style={{ padding: '0.5rem 1rem', borderRadius: '6px', backgroundColor: '#16a34a', color: 'white', fontWeight: '800', textDecoration: 'none', fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}
                        >
                          💾 Download MP4 Record (Auto-Purge from VPS)
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

          </div>
        )}

        {/* TAB 6: Digital Publications & Books Ingestion Panel */}
        {activeTab === ('publications' as any) && (
          <div style={{ backgroundColor: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', padding: '2rem', boxSizing: 'border-box' }}>
            <div style={{ marginBottom: '1.5rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                <h2 style={{ fontSize: '1.35rem', fontWeight: '800', color: '#0f172a', margin: '0 0 0.25rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <BookOpen size={24} color="#9333ea" /> Digital Publications & Books Ingestion (.json + QR)
                </h2>
                <p style={{ color: '#64748b', fontSize: '0.9rem', margin: 0 }}>
                  Upload scholarly law treatises, solution manuals, and monographs. Automatically converts into structured <strong>.JSON</strong> chapters and generates high-resolution QR codes for mobile downloads and reading.
                </p>
              </div>
              <a
                href="/downloads"
                target="_blank"
                rel="noreferrer"
                style={{ padding: '0.5rem 1rem', borderRadius: '8px', backgroundColor: '#9333ea', color: 'white', fontWeight: '800', textDecoration: 'none', fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}
              >
                <Download size={16} /> View Public Downloads Page ➔
              </a>
            </div>

            {/* Ingestion Form */}
            <form onSubmit={handlePublishPublication} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', marginBottom: '2.5rem' }}>
              
              <div style={{ backgroundColor: '#faf5ff', padding: '1.5rem', borderRadius: '12px', border: '2px dashed #9333ea', textAlign: 'center', boxSizing: 'border-box' }}>
                <BookOpen size={40} color="#9333ea" style={{ marginBottom: '0.5rem' }} />
                <label style={{ display: 'block', fontSize: '1rem', fontWeight: '800', color: '#7e22ce', cursor: 'pointer', marginBottom: '0.25rem' }}>
                  Select PDF Publication / Book from Device
                </label>
                <input
                  type="file"
                  required
                  accept=".pdf"
                  onChange={(e) => setPubPdfFile(e.target.files ? e.target.files[0] : null)}
                  style={{ display: 'inline-block', fontSize: '0.9rem' }}
                />
                {pubPdfFile && (
                  <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#6b21a8', fontWeight: '700' }}>
                    Selected: {pubPdfFile.name} ({Math.round(pubPdfFile.size / 1024)} KB)
                  </div>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '800', color: '#475569', marginBottom: '0.45rem' }}>
                    📖 Book / Publication Title
                  </label>
                  <input
                    type="text"
                    required
                    value={pubTitle}
                    onChange={(e) => setPubTitle(e.target.value)}
                    placeholder="e.g. CSS Islamic Studies Solution Manual"
                    style={{ width: '100%', height: '42px', padding: '0 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', boxSizing: 'border-box' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '800', color: '#475569', marginBottom: '0.45rem' }}>
                    📑 Subtitle / Examination Scope
                  </label>
                  <input
                    type="text"
                    value={pubSubtitle}
                    onChange={(e) => setPubSubtitle(e.target.value)}
                    placeholder="e.g. With Past Solved Papers (Years 2017 to 2026 Complete)"
                    style={{ width: '100%', height: '42px', padding: '0 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '800', color: '#475569', marginBottom: '0.45rem' }}>
                    ✍️ Authors & Legal Scholars (comma separated)
                  </label>
                  <input
                    type="text"
                    value={pubAuthors}
                    onChange={(e) => setPubAuthors(e.target.value)}
                    placeholder="e.g. Advocate Najeeb Elum, Advocate Naila Hafeez"
                    style={{ width: '100%', height: '42px', padding: '0 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', boxSizing: 'border-box' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '800', color: '#475569', marginBottom: '0.45rem' }}>
                    🏷️ Category / Subject Area
                  </label>
                  <select
                    value={pubCategory}
                    onChange={(e) => setPubCategory(e.target.value)}
                    style={{ width: '100%', height: '42px', padding: '0 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', boxSizing: 'border-box' }}
                  >
                    <option value="CSS Examination & Islamic Jurisprudence">CSS Examination & Islamic Jurisprudence</option>
                    <option value="Constitutional & Statutory Law">Constitutional & Statutory Law</option>
                    <option value="Civil & Criminal Jurisprudence">Civil & Criminal Jurisprudence</option>
                    <option value="Corporate & Tax Studies">Corporate & Tax Studies</option>
                    <option value="General Legal Monographs">General Legal Monographs</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '800', color: '#475569', marginBottom: '0.45rem' }}>
                    💰 Subscription / Purchase Amount (PKR)
                  </label>
                  <input
                    type="number"
                    value={pubPrice}
                    onChange={(e) => setPubPrice(e.target.value)}
                    placeholder="1500"
                    style={{ width: '100%', height: '42px', padding: '0 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '800', color: '#475569', marginBottom: '0.45rem' }}>
                  📝 Scholarly Description & Syllabus Synopsis
                </label>
                <textarea
                  rows={3}
                  value={pubDescription}
                  onChange={(e) => setPubDescription(e.target.value)}
                  placeholder="Provide a comprehensive academic summary, key topics, and research significance..."
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', boxSizing: 'border-box' }}
                />
              </div>

              <button
                type="submit"
                disabled={pubLoading}
                style={{ padding: '0.85rem 2rem', borderRadius: '8px', border: 'none', backgroundColor: '#9333ea', color: 'white', cursor: 'pointer', fontWeight: '800', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', alignSelf: 'flex-start' }}
              >
                <BookOpen size={18} /> {pubLoading ? 'Ingesting, Converting to JSON & Generating QR...' : '🚀 Ingest Publication, Convert to JSON & Publish'}
              </button>
            </form>

            {/* Table of Ingested Publications */}
            <h4 style={{ fontSize: '1.1rem', fontWeight: '800', color: '#0f172a', marginBottom: '1rem' }}>
              Ingested Digital Publications Catalog ({publicationsList.length})
            </h4>

            {publicationsList.length === 0 ? (
              <div style={{ color: '#64748b', padding: '1rem 0' }}>
                No publications found. Use the form above to ingest your first PDF publication.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {publicationsList.map((p) => (
                  <div key={p._id} style={{ backgroundColor: '#f8fafc', padding: '1.25rem', borderRadius: '12px', border: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                      {p.qrCodeUrl ? (
                        <img src={p.qrCodeUrl} alt="QR" style={{ width: '64px', height: '64px', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: 'white', padding: '2px' }} />
                      ) : (
                        <div style={{ width: '64px', height: '64px', borderRadius: '8px', backgroundColor: '#f3e8ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9333ea' }}>
                          <QrCode size={32} />
                        </div>
                      )}
                      <div>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.25rem' }}>
                          <span style={{ backgroundColor: '#9333ea', color: 'white', fontSize: '0.7rem', padding: '0.15rem 0.5rem', borderRadius: '4px', fontWeight: '800' }}>
                            {p.category}
                          </span>
                          <span style={{ backgroundColor: '#fef3c7', color: '#92400e', fontSize: '0.75rem', padding: '0.15rem 0.5rem', borderRadius: '4px', fontWeight: '800' }}>
                            PKR {p.price ? p.price.toLocaleString() : '1,500'}
                          </span>
                          <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                            📄 {p.pageCount || 112} Pages • 💾 {Math.round((p.fileSizeBytes || 598017) / 1024)} KB
                          </span>
                        </div>
                        <h4 style={{ fontSize: '1.05rem', fontWeight: '800', color: '#0f172a', margin: '0 0 0.25rem 0' }}>
                          {p.title}
                        </h4>
                        <p style={{ fontSize: '0.82rem', color: '#475569', margin: 0 }}>
                          Authors: {Array.isArray(p.authors) ? p.authors.join(', ') : p.authors}
                        </p>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <a
                        href="/downloads"
                        target="_blank"
                        rel="noreferrer"
                        style={{ padding: '0.5rem 0.85rem', borderRadius: '6px', backgroundColor: '#3b82f6', color: 'white', fontWeight: '800', textDecoration: 'none', fontSize: '0.82rem', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}
                      >
                        👁️ View in Downloads
                      </a>
                      <button
                        type="button"
                        onClick={() => handleDownloadPublication(p)}
                        disabled={downloadingPubId === p._id}
                        style={{ padding: '0.5rem 0.85rem', borderRadius: '6px', border: 'none', backgroundColor: '#10b981', color: 'white', fontWeight: '800', cursor: downloadingPubId === p._id ? 'wait' : 'pointer', fontSize: '0.82rem', display: 'inline-flex', alignItems: 'center', gap: '0.3rem', boxShadow: '0 2px 6px rgba(16,185,129,0.3)' }}
                        title="Download complete publication PDF (Admin Mode)"
                      >
                        <Download size={14} /> {downloadingPubId === p._id ? 'Downloading...' : 'Download PDF'}
                      </button>
                      <button
                        onClick={() => handleDeletePublication(p._id)}
                        style={{ padding: '0.5rem 0.75rem', borderRadius: '6px', border: 'none', backgroundColor: '#fee2e2', color: '#dc2626', fontWeight: '800', cursor: 'pointer', fontSize: '0.82rem', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}
                      >
                        <Trash2 size={14} /> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

          </div>
        )}

        {/* TAB 7: Subscription Receipts & Approvals Panel */}
        {activeTab === 'subscriptions' && (
          <div style={{ backgroundColor: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', padding: '2rem', boxSizing: 'border-box' }}>
            
            {/* Header & Verification Guidance */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '1.25rem', marginBottom: '1.5rem' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ backgroundColor: '#059669', color: 'white', fontSize: '0.75rem', fontWeight: '800', padding: '0.2rem 0.6rem', borderRadius: '4px' }}>
                    FINANCIAL AUDIT & ACCESS DISPOSAL
                  </span>
                  <h3 style={{ fontSize: '1.3rem', fontWeight: '800', color: '#0f172a', margin: 0 }}>
                    💳 Book Subscriptions & Payment Receipts Approval
                  </h3>
                </div>
                <p style={{ color: '#64748b', fontSize: '0.85rem', margin: '0.35rem 0 0 0' }}>
                  Verify subscriber payment deposits into Owner / Superuser accounts. Once confirmed and approved, full-text PDF download access is permanently unlocked for the subscriber.
                </p>
              </div>

              <button
                onClick={fetchPaymentsList}
                disabled={paymentsLoading}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '8px',
                  backgroundColor: '#f1f5f9',
                  border: '1px solid #cbd5e1',
                  color: '#334155',
                  fontWeight: '700',
                  fontSize: '0.82rem',
                  cursor: paymentsLoading ? 'wait' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem'
                }}
              >
                <RefreshCw size={14} /> Refresh Receipts
              </button>
            </div>

            {/* Designated Superuser Accounts Reference Card */}
            <div style={{
              backgroundColor: '#f8fafc',
              border: '1.5px solid #cbd5e1',
              borderRadius: '12px',
              padding: '1.25rem',
              marginBottom: '1.5rem',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '1rem'
            }}>
              <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.85rem' }}>
                <span style={{ fontSize: '0.72rem', fontWeight: '800', color: '#d97706', textTransform: 'uppercase' }}>JazzCash Account (Superuser)</span>
                <div style={{ fontSize: '1rem', fontWeight: '800', color: '#0f172a', marginTop: '2px' }}>0333 9389948</div>
                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Title: NAJEEB ULLAH</div>
              </div>

              <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.85rem' }}>
                <span style={{ fontSize: '0.72rem', fontWeight: '800', color: '#059669', textTransform: 'uppercase' }}>EasyPaisa Account (Superuser)</span>
                <div style={{ fontSize: '1rem', fontWeight: '800', color: '#0f172a', marginTop: '2px' }}>0333 9389948</div>
                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Title: NAJEEB ULLAH</div>
              </div>

              <div style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.85rem' }}>
                <span style={{ fontSize: '0.72rem', fontWeight: '800', color: '#0284c7', textTransform: 'uppercase' }}>Allied Bank (ABL) Transfer</span>
                <div style={{ fontSize: '0.88rem', fontWeight: '800', color: '#0f172a', marginTop: '2px', fontFamily: 'monospace' }}>PK75ABPA0010118975340013</div>
                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Title: NAJEEB ULLAH (Branch: 0189)</div>
              </div>
            </div>

            {/* Filter Toggle Buttons */}
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
              <button
                onClick={() => setPaymentFilter('pending')}
                style={{
                  padding: '0.45rem 0.9rem',
                  borderRadius: '20px',
                  border: 'none',
                  backgroundColor: paymentFilter === 'pending' ? '#dc2626' : '#f1f5f9',
                  color: paymentFilter === 'pending' ? 'white' : '#475569',
                  fontSize: '0.82rem',
                  fontWeight: '800',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.35rem'
                }}
              >
                Pending Verification ({paymentsList.filter(p => p.status === 'PENDING_APPROVAL').length})
              </button>

              <button
                onClick={() => setPaymentFilter('completed')}
                style={{
                  padding: '0.45rem 0.9rem',
                  borderRadius: '20px',
                  border: 'none',
                  backgroundColor: paymentFilter === 'completed' ? '#059669' : '#f1f5f9',
                  color: paymentFilter === 'completed' ? 'white' : '#475569',
                  fontSize: '0.82rem',
                  fontWeight: '800',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.35rem'
                }}
              >
                Approved & Unlocked ({paymentsList.filter(p => p.status === 'COMPLETED' || p.status === 'APPROVED').length})
              </button>

              <button
                onClick={() => setPaymentFilter('rejected')}
                style={{
                  padding: '0.45rem 0.9rem',
                  borderRadius: '20px',
                  border: 'none',
                  backgroundColor: paymentFilter === 'rejected' ? '#475569' : '#f1f5f9',
                  color: paymentFilter === 'rejected' ? 'white' : '#475569',
                  fontSize: '0.82rem',
                  fontWeight: '800',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.35rem'
                }}
              >
                Rejected ({paymentsList.filter(p => p.status === 'REJECTED').length})
              </button>

              <button
                onClick={() => setPaymentFilter('all')}
                style={{
                  padding: '0.45rem 0.9rem',
                  borderRadius: '20px',
                  border: 'none',
                  backgroundColor: paymentFilter === 'all' ? '#1e293b' : '#f1f5f9',
                  color: paymentFilter === 'all' ? 'white' : '#475569',
                  fontSize: '0.82rem',
                  fontWeight: '800',
                  cursor: 'pointer'
                }}
              >
                All Records ({paymentsList.length})
              </button>
            </div>

            {/* List of Payments */}
            {(() => {
              const filtered = paymentsList.filter(p => {
                if (paymentFilter === 'pending') return p.status === 'PENDING_APPROVAL';
                if (paymentFilter === 'completed') return p.status === 'COMPLETED' || p.status === 'APPROVED';
                if (paymentFilter === 'rejected') return p.status === 'REJECTED';
                return true;
              });

              if (filtered.length === 0) {
                return (
                  <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#64748b', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px dashed #cbd5e1' }}>
                    <Shield size={36} color="#94a3b8" style={{ marginBottom: '0.5rem' }} />
                    <h4 style={{ fontSize: '1.05rem', fontWeight: '700', color: '#334155', margin: '0 0 0.25rem 0' }}>
                      No {paymentFilter === 'all' ? '' : paymentFilter} subscription payments found
                    </h4>
                    <p style={{ fontSize: '0.85rem', margin: 0 }}>
                      Subscribers who submit payment proofs via JazzCash, EasyPaisa, or Bank Transfer will appear here for verification.
                    </p>
                  </div>
                );
              }

              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                  {filtered.map((p) => {
                    let meta: any = {};
                    try {
                      if (p.approvalComments) meta = JSON.parse(p.approvalComments);
                    } catch (e) {}

                    const isPending = p.status === 'PENDING_APPROVAL';
                    const isCompleted = p.status === 'COMPLETED' || p.status === 'APPROVED';
                    const isRejected = p.status === 'REJECTED';

                    const subscriberEmail = meta.email || p.payer?.email || 'Unknown';
                    const senderName = meta.senderName || p.payer?.name || 'Valued Subscriber';
                    const senderPhone = meta.senderPhone || p.payer?.phone || 'N/A';
                    const receiptUrl = meta.receiptUrl;
                    const bookTitle = meta.publicationTitle || 'CSS Islamic Studies Solution Manual';
                    const gateway = meta.gateway || 'JazzCash';

                    return (
                      <div
                        key={p.id}
                        style={{
                          backgroundColor: '#f8fafc',
                          border: isPending ? '2px solid #f87171' : isCompleted ? '1.5px solid #86efac' : '1px solid #cbd5e1',
                          borderRadius: '14px',
                          padding: '1.25rem',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '1rem',
                          boxShadow: '0 2px 6px rgba(0,0,0,0.04)'
                        }}
                      >
                        {/* TOP ROW: STATUS, PUBLICATION & AMOUNT */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.75rem' }}>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem', flexWrap: 'wrap' }}>
                              <span style={{
                                backgroundColor: isPending ? '#dc2626' : isCompleted ? '#16a34a' : '#64748b',
                                color: 'white',
                                fontSize: '0.72rem',
                                fontWeight: '900',
                                padding: '0.2rem 0.6rem',
                                borderRadius: '6px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                              }}>
                                {isPending ? '⏳ PENDING OWNER APPROVAL' : isCompleted ? '✅ APPROVED & UNLOCKED' : '❌ REJECTED'}
                              </span>

                              <span style={{
                                backgroundColor: '#1e293b',
                                color: '#ecc94b',
                                fontSize: '0.75rem',
                                fontWeight: '800',
                                padding: '0.2rem 0.6rem',
                                borderRadius: '6px'
                              }}>
                                💳 {gateway}
                              </span>

                              <span style={{ fontSize: '0.75rem', color: '#64748b', fontFamily: 'monospace' }}>
                                TRX / TID: <strong>{p.transactionId || 'N/A'}</strong>
                              </span>
                            </div>

                            <h4 style={{ fontSize: '1.1rem', fontWeight: '800', color: '#0f172a', margin: '0 0 0.2rem 0' }}>
                              📚 {bookTitle}
                            </h4>

                            <div style={{ fontSize: '0.82rem', color: '#475569' }}>
                              Subscriber Email: <strong style={{ color: '#0284c7' }}>{subscriberEmail}</strong>
                            </div>
                          </div>

                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '1.35rem', fontWeight: '900', color: '#059669' }}>
                              PKR {p.amount ? Number(p.amount).toLocaleString() : '1,500'}
                            </div>
                            <span style={{ fontSize: '0.72rem', color: '#64748b' }}>
                              Submitted: {new Date(p.createdAt || Date.now()).toLocaleString()}
                            </span>
                          </div>
                        </div>

                        {/* MIDDLE ROW: SENDER DETAILS & RECEIPT SLIP PREVIEW */}
                        <div style={{
                          backgroundColor: 'white',
                          border: '1px solid #e2e8f0',
                          borderRadius: '10px',
                          padding: '0.85rem 1.15rem',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: '1rem'
                        }}>
                          <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
                            <div>
                              <span style={{ display: 'block', fontSize: '0.7rem', color: '#64748b', fontWeight: '700', textTransform: 'uppercase' }}>
                                Sender Name
                              </span>
                              <strong style={{ fontSize: '0.9rem', color: '#0f172a' }}>{senderName}</strong>
                            </div>

                            <div>
                              <span style={{ display: 'block', fontSize: '0.7rem', color: '#64748b', fontWeight: '700', textTransform: 'uppercase' }}>
                                Sender Mobile / Account
                              </span>
                              <strong style={{ fontSize: '0.9rem', color: '#0f172a' }}>{senderPhone}</strong>
                            </div>

                            <div>
                              <span style={{ display: 'block', fontSize: '0.7rem', color: '#64748b', fontWeight: '700', textTransform: 'uppercase' }}>
                                Transfer Destination
                              </span>
                              <strong style={{ fontSize: '0.88rem', color: '#d97706' }}>NAJEEB ULLAH (+92 333 9389948)</strong>
                            </div>
                          </div>

                          {/* RECEIPT SLIP PREVIEW THUMBNAIL */}
                          {receiptUrl ? (
                            <button
                              type="button"
                              onClick={() => setReceiptModalUrl(receiptUrl)}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.4rem',
                                padding: '0.45rem 0.85rem',
                                borderRadius: '8px',
                                backgroundColor: '#f0fdf4',
                                border: '1px solid #86efac',
                                color: '#15803d',
                                fontWeight: '800',
                                fontSize: '0.82rem',
                                cursor: 'pointer'
                              }}
                            >
                              <span>📄 View Receipt Slip</span>
                            </button>
                          ) : (
                            <span style={{ fontSize: '0.75rem', color: '#94a3b8', fontStyle: 'italic' }}>
                              No screenshot attached (TRX ID verification only)
                            </span>
                          )}
                        </div>

                        {/* REJECTION REASON IF ANY */}
                        {isRejected && (
                          <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '0.65rem 0.85rem', fontSize: '0.8rem', color: '#991b1b' }}>
                            <strong>Rejection Note:</strong> {p.approvalComments || 'Transfer not found in account.'}
                          </div>
                        )}

                        {/* BOTTOM ACTIONS FOR SUPERUSER */}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', flexWrap: 'wrap', paddingTop: '0.35rem' }}>
                          {isPending && (
                            <>
                              <button
                                type="button"
                                onClick={() => handleApprovePayment(p)}
                                disabled={approvingPaymentId === p.id}
                                style={{
                                  padding: '0.65rem 1.35rem',
                                  borderRadius: '8px',
                                  border: 'none',
                                  backgroundColor: '#059669',
                                  color: 'white',
                                  fontWeight: '900',
                                  fontSize: '0.88rem',
                                  cursor: approvingPaymentId === p.id ? 'wait' : 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.4rem',
                                  boxShadow: '0 2px 8px rgba(5,150,105,0.3)'
                                }}
                              >
                                <CheckCircle size={16} />
                                {approvingPaymentId === p.id ? 'Approving & Unlocking Access...' : '✅ Confirm Deposit & Unlock Download'}
                              </button>

                              <button
                                type="button"
                                onClick={() => handleRejectPayment(p)}
                                disabled={rejectingPaymentId === p.id}
                                style={{
                                  padding: '0.65rem 1rem',
                                  borderRadius: '8px',
                                  border: '1px solid #fca5a5',
                                  backgroundColor: '#fef2f2',
                                  color: '#dc2626',
                                  fontWeight: '800',
                                  fontSize: '0.85rem',
                                  cursor: rejectingPaymentId === p.id ? 'wait' : 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.4rem'
                                }}
                              >
                                <AlertCircle size={16} />
                                {rejectingPaymentId === p.id ? 'Rejecting...' : '❌ Reject Proof'}
                              </button>
                            </>
                          )}

                          {isCompleted && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#16a34a', fontWeight: '800', fontSize: '0.85rem' }}>
                              <CheckCircle size={16} />
                              <span>Access Unlocked for {subscriberEmail}</span>
                            </div>
                          )}
                        </div>

                      </div>
                    );
                  })}
                </div>
              );
            })()}

          </div>
        )}

        {/* TAB 8: User Registration Approvals Panel */}
        {activeTab === 'registrations' && (
          <div style={{ backgroundColor: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', padding: '2rem', boxSizing: 'border-box' }}>
            
            {/* Header & Verification Guidance */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '1.25rem', marginBottom: '1.5rem' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ backgroundColor: '#2563eb', color: 'white', fontSize: '0.75rem', fontWeight: '800', padding: '0.2rem 0.6rem', borderRadius: '4px' }}>
                    IDENTITY & BAR ENROLLMENT VERIFICATION
                  </span>
                  <h3 style={{ fontSize: '1.3rem', fontWeight: '800', color: '#0f172a', margin: 0 }}>
                    User Registration Approvals ({pendingUsers.length} Pending)
                  </h3>
                </div>
                <p style={{ margin: '0.35rem 0 0', color: '#64748b', fontSize: '0.85rem' }}>
                  Review new user registration requests, inspect submitted CNIC and Bar Council credentials, and approve or reject accounts.
                </p>
              </div>

              <button
                onClick={fetchPendingUsers}
                disabled={loadingPendingUsers}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '8px',
                  backgroundColor: '#f8fafc',
                  border: '1px solid #cbd5e1',
                  color: '#334155',
                  fontWeight: '700',
                  fontSize: '0.85rem',
                  cursor: loadingPendingUsers ? 'wait' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem'
                }}
              >
                <RefreshCw size={15} style={{ animation: loadingPendingUsers ? 'spin 1s linear infinite' : 'none' }} />
                <span>Refresh Queue</span>
              </button>
            </div>

            {/* Content List */}
            {loadingPendingUsers ? (
              <div style={{ padding: '3rem 1rem', textAlign: 'center', color: '#64748b' }}>
                <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite', margin: '0 auto 0.75rem' }} />
                <p style={{ margin: 0, fontWeight: '700' }}>Loading pending registrations from database...</p>
              </div>
            ) : pendingUsers.length === 0 ? (
              <div style={{ padding: '3.5rem 1rem', textAlign: 'center', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px dashed #cbd5e1' }}>
                <CheckCircle size={36} color="#16a34a" style={{ margin: '0 auto 0.75rem' }} />
                <h4 style={{ margin: '0 0 0.25rem', color: '#0f172a', fontSize: '1.1rem', fontWeight: '800' }}>
                  No Pending User Registrations
                </h4>
                <p style={{ margin: 0, color: '#64748b', fontSize: '0.85rem' }}>
                  All applicants have been evaluated. New registration submissions will appear here in real-time.
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {pendingUsers.map(user => {
                  return (
                    <div
                      key={user.id}
                      style={{
                        border: '1px solid #e2e8f0',
                        borderRadius: '12px',
                        padding: '1.25rem',
                        backgroundColor: '#ffffff',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '1rem'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.75rem' }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                            <h4 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '800', color: '#0f172a' }}>
                              {user.name}
                            </h4>
                            <span style={{
                              backgroundColor: '#e0e7ff',
                              color: '#3730a3',
                              fontSize: '0.75rem',
                              fontWeight: '800',
                              padding: '0.15rem 0.5rem',
                              borderRadius: '4px',
                              textTransform: 'uppercase'
                            }}>
                              {user.role}
                            </span>
                            <span style={{
                              backgroundColor: '#fef3c7',
                              color: '#92400e',
                              fontSize: '0.72rem',
                              fontWeight: '700',
                              padding: '0.15rem 0.5rem',
                              borderRadius: '4px'
                            }}>
                              ⏳ Pending Approval
                            </span>
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginTop: '0.5rem', fontSize: '0.82rem', color: '#475569' }}>
                            <div>📧 Email: <strong style={{ color: '#0284c7' }}>{user.email}</strong></div>
                            <div>📞 Phone: <strong>{user.phone || 'N/A'}</strong></div>
                            <div>🪪 CNIC: <strong style={{ fontFamily: 'monospace' }}>{user.cnic || 'N/A'}</strong></div>
                            <div>⚖️ Bar License: <strong style={{ fontFamily: 'monospace' }}>{user.barCouncilId || 'N/A'}</strong></div>
                            <div>📍 Location: <strong>{user.city || 'N/A'}{user.district ? `, ${user.district}` : ''}</strong></div>
                            <div>📅 Applied: <strong>{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Recent'}</strong></div>
                          </div>
                        </div>

                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedPendingUser(user);
                              setPendingUserRejectionComments('');
                            }}
                            style={{
                              padding: '0.55rem 0.9rem',
                              borderRadius: '8px',
                              border: '1px solid #0284c7',
                              backgroundColor: '#f0f9ff',
                              color: '#0284c7',
                              fontWeight: '700',
                              fontSize: '0.82rem',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.35rem'
                            }}
                          >
                            <Info size={15} />
                            <span>Inspect Credentials</span>
                          </button>

                          <button
                            type="button"
                            onClick={() => handleApprovePendingUser(user)}
                            disabled={approvingUserId === user.id}
                            style={{
                              padding: '0.55rem 1rem',
                              borderRadius: '8px',
                              border: 'none',
                              backgroundColor: '#16a34a',
                              color: 'white',
                              fontWeight: '800',
                              fontSize: '0.82rem',
                              cursor: approvingUserId === user.id ? 'wait' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.35rem'
                            }}
                          >
                            <CheckCircle size={15} />
                            <span>{approvingUserId === user.id ? 'Activating...' : 'Approve & Activate'}</span>
                          </button>

                          <button
                            type="button"
                            onClick={() => handleRejectPendingUser(user)}
                            disabled={rejectingUserId === user.id}
                            style={{
                              padding: '0.55rem 0.9rem',
                              borderRadius: '8px',
                              border: '1px solid #fca5a5',
                              backgroundColor: '#fef2f2',
                              color: '#dc2626',
                              fontWeight: '800',
                              fontSize: '0.82rem',
                              cursor: rejectingUserId === user.id ? 'wait' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.35rem'
                            }}
                          >
                            <AlertCircle size={15} />
                            <span>{rejectingUserId === user.id ? 'Rejecting...' : 'Reject'}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

          </div>
        )}

        {/* MODAL: HIGH-RESOLUTION RECEIPT SLIP VIEWER */}
        {receiptModalUrl && (
          <div
            onClick={() => setReceiptModalUrl(null)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.85)',
              zIndex: 999999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '1.5rem'
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                maxWidth: '650px',
                width: '100%',
                maxHeight: '90vh',
                overflowY: 'auto',
                padding: '1.5rem',
                position: 'relative'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h4 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '800', color: '#0f172a' }}>
                  📄 Subscriber Transfer Receipt / Deposit Slip
                </h4>
                <button
                  onClick={() => setReceiptModalUrl(null)}
                  style={{
                    backgroundColor: '#f1f5f9',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '0.35rem 0.65rem',
                    cursor: 'pointer',
                    fontWeight: '800'
                  }}
                >
                  ✕ Close
                </button>
              </div>

              <div style={{ textAlign: 'center', backgroundColor: '#0f172a', borderRadius: '8px', padding: '1rem' }}>
                <img
                  src={receiptModalUrl}
                  alt="Payment Receipt Slip"
                  style={{ maxWidth: '100%', maxHeight: '65vh', objectFit: 'contain', borderRadius: '4px' }}
                />
              </div>

              <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <a
                  href={receiptModalUrl}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    color: '#0284c7',
                    fontWeight: '700',
                    fontSize: '0.85rem',
                    textDecoration: 'none'
                  }}
                >
                  🔗 Open full image in new tab
                </a>
                <button
                  onClick={() => setReceiptModalUrl(null)}
                  style={{
                    padding: '0.5rem 1.25rem',
                    borderRadius: '8px',
                    backgroundColor: '#1e293b',
                    color: 'white',
                    border: 'none',
                    fontWeight: '800',
                    fontSize: '0.85rem',
                    cursor: 'pointer'
                  }}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}

        {/* MODAL: APPLICANT CREDENTIALS & DOCUMENT INSPECTION */}
        {selectedPendingUser && (
          <div
            onClick={() => setSelectedPendingUser(null)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.8)',
              zIndex: 999998,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '1.5rem'
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                maxWidth: '750px',
                width: '100%',
                maxHeight: '90vh',
                overflowY: 'auto',
                padding: '1.75rem',
                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
                position: 'relative'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: '1rem', marginBottom: '1.25rem' }}>
                <div>
                  <span style={{ fontSize: '0.75rem', fontWeight: '800', color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Identity Verification File
                  </span>
                  <h3 style={{ fontSize: '1.35rem', fontWeight: '800', color: '#0f172a', margin: '0.2rem 0 0' }}>
                    {selectedPendingUser.name} ({selectedPendingUser.role})
                  </h3>
                </div>
                <button
                  onClick={() => setSelectedPendingUser(null)}
                  style={{
                    backgroundColor: '#f1f5f9',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '0.35rem 0.65rem',
                    cursor: 'pointer',
                    fontWeight: '800',
                    fontSize: '1rem'
                  }}
                >
                  ✕
                </button>
              </div>

              {/* CREDENTIAL DETAILS GRID */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', backgroundColor: '#f8fafc', padding: '1.25rem', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '1.5rem' }}>
                <div>
                  <span style={{ fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '700' }}>Email Address</span>
                  <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#0284c7' }}>{selectedPendingUser.email}</div>
                </div>

                <div>
                  <span style={{ fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '700' }}>Phone / WhatsApp</span>
                  <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#16a34a' }}>{selectedPendingUser.phone || 'N/A'}</div>
                </div>

                <div>
                  <span style={{ fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '700' }}>CNIC Number</span>
                  <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#0f172a', fontFamily: 'monospace' }}>{selectedPendingUser.cnic || 'N/A'}</div>
                </div>

                <div>
                  <span style={{ fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '700' }}>CNIC Expiry</span>
                  <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#334155' }}>{selectedPendingUser.cnicExpiry || 'Not Provided'}</div>
                </div>

                <div>
                  <span style={{ fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '700' }}>Bar License ID</span>
                  <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#d97706', fontFamily: 'monospace' }}>{selectedPendingUser.barCouncilId || 'N/A'}</div>
                </div>

                <div>
                  <span style={{ fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '700' }}>Bar License Expiry</span>
                  <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#334155' }}>{selectedPendingUser.barCouncilExpiry || 'Not Provided'}</div>
                </div>

                <div>
                  <span style={{ fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '700' }}>City / District</span>
                  <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#334155' }}>{selectedPendingUser.city || 'N/A'}{selectedPendingUser.district ? `, ${selectedPendingUser.district}` : ''}</div>
                </div>

                <div>
                  <span style={{ fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', fontWeight: '700' }}>Applied Date</span>
                  <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#334155' }}>{selectedPendingUser.createdAt ? new Date(selectedPendingUser.createdAt).toLocaleString() : 'N/A'}</div>
                </div>
              </div>

              {/* UPLOADED DOCUMENTS PREVIEW BOXES */}
              <h4 style={{ fontSize: '0.95rem', fontWeight: '800', color: '#0f172a', marginBottom: '0.75rem' }}>
                📄 Uploaded CNIC & Advocate Bar License Cards
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                <div style={{ backgroundColor: '#f8fafc', padding: '0.75rem', borderRadius: '8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                  <span style={{ fontSize: '0.75rem', color: '#64748b', display: 'block', marginBottom: '0.4rem', fontWeight: '700' }}>CNIC Front</span>
                  <button
                    onClick={() => setPendingUserDocPreview(selectedPendingUser.cnicFrontUrl || 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=600')}
                    style={{ padding: '0.4rem 0.75rem', backgroundColor: '#e0f2fe', color: '#0369a1', border: '1px solid #7dd3fc', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 'bold', cursor: 'pointer', width: '100%' }}
                  >
                    🖼️ View Front
                  </button>
                </div>

                <div style={{ backgroundColor: '#f8fafc', padding: '0.75rem', borderRadius: '8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                  <span style={{ fontSize: '0.75rem', color: '#64748b', display: 'block', marginBottom: '0.4rem', fontWeight: '700' }}>CNIC Back</span>
                  <button
                    onClick={() => setPendingUserDocPreview(selectedPendingUser.cnicBackUrl || 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=600')}
                    style={{ padding: '0.4rem 0.75rem', backgroundColor: '#e0f2fe', color: '#0369a1', border: '1px solid #7dd3fc', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 'bold', cursor: 'pointer', width: '100%' }}
                  >
                    🖼️ View Back
                  </button>
                </div>

                <div style={{ backgroundColor: '#f8fafc', padding: '0.75rem', borderRadius: '8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                  <span style={{ fontSize: '0.75rem', color: '#64748b', display: 'block', marginBottom: '0.4rem', fontWeight: '700' }}>Bar Card Front</span>
                  <button
                    onClick={() => setPendingUserDocPreview(selectedPendingUser.barCardFrontUrl || 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=600')}
                    style={{ padding: '0.4rem 0.75rem', backgroundColor: '#fef3c7', color: '#92400e', border: '1px solid #fde68a', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 'bold', cursor: 'pointer', width: '100%' }}
                  >
                    ⚖️ View Card
                  </button>
                </div>

                <div style={{ backgroundColor: '#f8fafc', padding: '0.75rem', borderRadius: '8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                  <span style={{ fontSize: '0.75rem', color: '#64748b', display: 'block', marginBottom: '0.4rem', fontWeight: '700' }}>Bar Card Back</span>
                  <button
                    onClick={() => setPendingUserDocPreview(selectedPendingUser.barCardBackUrl || 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=600')}
                    style={{ padding: '0.4rem 0.75rem', backgroundColor: '#fef3c7', color: '#92400e', border: '1px solid #fde68a', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 'bold', cursor: 'pointer', width: '100%' }}
                  >
                    ⚖️ View Card
                  </button>
                </div>
              </div>

              {/* REJECTION COMMENTS / ADDITIONAL REQUIREMENTS BOX */}
              <div style={{ backgroundColor: '#fff1f2', padding: '1rem', borderRadius: '10px', border: '1px solid #fecdd3', marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '800', color: '#be123c', marginBottom: '0.4rem' }}>
                  💬 Administrator Decision Comments / Additional Requirements (Dispatched via email if Rejected):
                </label>
                <textarea 
                  rows={3} 
                  value={pendingUserRejectionComments}
                  onChange={(e) => setPendingUserRejectionComments(e.target.value)}
                  placeholder="Specify rejection reason or missing requirements (e.g. Uploaded CNIC front image is blurry. Please upload clear scan of Balochistan Bar License)." 
                  style={{ width: '100%', padding: '0.75rem', backgroundColor: 'white', color: '#0f172a', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '0.85rem', outline: 'none', boxSizing: 'border-box' }}
                />
              </div>

              {/* ACTION BUTTONS */}
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <button 
                  onClick={() => setSelectedPendingUser(null)}
                  style={{ padding: '0.65rem 1.25rem', backgroundColor: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  Close
                </button>

                <button 
                  onClick={() => handleRejectPendingUser(selectedPendingUser)}
                  disabled={rejectingUserId === selectedPendingUser.id}
                  style={{ padding: '0.65rem 1.25rem', backgroundColor: '#dc2626', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  {rejectingUserId === selectedPendingUser.id ? 'Rejecting...' : 'Reject Registration'}
                </button>

                <button 
                  onClick={() => handleApprovePendingUser(selectedPendingUser)}
                  disabled={approvingUserId === selectedPendingUser.id}
                  style={{ padding: '0.65rem 1.25rem', backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  {approvingUserId === selectedPendingUser.id ? 'Approving...' : 'Approve & Activate'}
                </button>
              </div>

            </div>
          </div>
        )}

        {/* MODAL: DOCUMENT HIGH-RES PREVIEW */}
        {pendingUserDocPreview && (
          <div
            onClick={() => setPendingUserDocPreview(null)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.85)',
              zIndex: 999999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '1.5rem'
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                maxWidth: '650px',
                width: '100%',
                maxHeight: '90vh',
                overflowY: 'auto',
                padding: '1.5rem',
                position: 'relative'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h4 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '800', color: '#0f172a' }}>
                  📄 Applicant Verification Document
                </h4>
                <button
                  onClick={() => setPendingUserDocPreview(null)}
                  style={{
                    backgroundColor: '#f1f5f9',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '0.35rem 0.65rem',
                    cursor: 'pointer',
                    fontWeight: '800'
                  }}
                >
                  ✕ Close
                </button>
              </div>

              <div style={{ textAlign: 'center', backgroundColor: '#0f172a', borderRadius: '8px', padding: '1rem' }}>
                <img
                  src={pendingUserDocPreview}
                  alt="Applicant Verification Document"
                  style={{ maxWidth: '100%', maxHeight: '65vh', objectFit: 'contain', borderRadius: '4px' }}
                />
              </div>

              <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <a
                  href={pendingUserDocPreview}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    color: '#0284c7',
                    fontWeight: '700',
                    fontSize: '0.85rem',
                    textDecoration: 'none'
                  }}
                >
                  🔗 Open full image in new tab
                </a>
                <button
                  onClick={() => setPendingUserDocPreview(null)}
                  style={{
                    padding: '0.5rem 1.25rem',
                    borderRadius: '8px',
                    backgroundColor: '#1e293b',
                    color: 'white',
                    border: 'none',
                    fontWeight: '800',
                    fontSize: '0.85rem',
                    cursor: 'pointer'
                  }}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default AdminUploadPortal;

